/**
 * Recent Changes Widget (issue #1657, part of epic #1650)
 *
 * Audit-trail activity feed over the trailing 7 days (server default),
 * matching `homepage-dashboard-spec.md`'s "Recent Model Changes" widget.
 * Data access is scoped identically to the existing audit query — see
 * `backend/src/graphql/resolvers/recentChanges.ts`'s module doc. Follows the
 * single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 */

import { useRecentChanges, type RecentChangeEntry } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './RecentChangesWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'recent-changes',
  title: 'Recent Changes',
  size: 'medium',
  refreshInterval: 300000, // 5 minutes — a change feed is more time-sensitive than the aggregate-stat widgets
  showRefresh: true,
  showExpand: false,
};

interface RecentChangesStats {
  changes: RecentChangeEntry[];
  total: number;
}

type RecentChangesQueryData = ReturnType<typeof useRecentChanges>['data'];

function extractRecentChangesStats(data: RecentChangesQueryData): RecentChangesStats | null {
  if (!data?.recentChanges) {
    return null;
  }
  return data.recentChanges;
}

/** Formats an ISO timestamp for display; falls back to the raw string if unparseable. */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  return date.toLocaleString();
}

export function RecentChangesWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useRecentChanges(7),
    extractRecentChangesStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.changes.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="recent-changes-widget__empty">
          <IconElement name="history" className="recent-changes-widget__empty-icon" />
          <p className="forge-typography--body2">No recent changes in the last 7 days</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="recent-changes-widget__content">
        <p
          className="recent-changes-widget__total forge-typography--caption"
          data-testid="recent-changes-total"
        >
          {stats.total} change{stats.total === 1 ? '' : 's'} in the last 7 days
        </p>

        <ul className="recent-changes-widget__list" data-testid="recent-changes-list">
          {stats.changes.map((change) => (
            <li key={change.eventId} className="recent-changes-widget__item">
              <IconElement
                name="edit"
                className="recent-changes-widget__item-icon"
                aria-hidden="true"
              />
              <div className="recent-changes-widget__item-body">
                <span className="recent-changes-widget__item-summary forge-typography--body2">
                  {change.summary}
                </span>
                <span className="recent-changes-widget__item-meta forge-typography--caption">
                  {change.actorName} · {formatTimestamp(change.timestamp)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </DashboardWidget>
  );
}
